import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { quotationsApi } from '../services/api';
import { CotacaoCreateDto } from '../types';

const QuotationForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Estados para valores formatados (display)
  const [displayValues, setDisplayValues] = useState({
    soja: '',
    arroz: '',
    milho: '',
  });

  // Estados para valores numéricos (envio)
  const [formData, setFormData] = useState<CotacaoCreateDto>({
    soja: 0,
    arroz: 0,
    milho: 0,
  });

  // Função para formatar valor monetário brasileiro
  const formatCurrency = (value: string): string => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');

    if (!numbers) return '';

    // Converte para número e divide por 100 (centavos)
    const amount = parseFloat(numbers) / 100;

    // Formata com separador de milhares e decimais
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Função para converter valor formatado para número
  const parseCurrency = (value: string): number => {
    const numbers = value.replace(/\D/g, '');
    return numbers ? parseFloat(numbers) / 100 : 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Formata o valor para exibição
    const formatted = formatCurrency(value);

    // Atualiza o valor formatado
    setDisplayValues(prev => ({
      ...prev,
      [name]: formatted
    }));

    // Atualiza o valor numérico
    setFormData(prev => ({
      ...prev,
      [name]: parseCurrency(value)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await quotationsApi.create(formData);
      setSuccess(true);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/admin/quotations');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar cotação');
      console.error('Error saving quotation:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <Button variant="link" onClick={() => navigate('/admin/quotations')} className="p-0 mb-2">
            <i className="bi bi-arrow-left me-2"></i>
            Voltar para lista
          </Button>
          <h2>Nova Cotação</h2>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success">
          Cotação cadastrada com sucesso! Redirecionando...
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={4}>
            <Card className="mb-4">
              <Card.Header className="bg-success text-white">
                <i className="bi bi-currency-dollar me-2"></i>
                Soja
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label>Preço (R$/sc 60kg)</Form.Label>
                  <Form.Control
                    type="text"
                    name="soja"
                    value={displayValues.soja}
                    onChange={handleChange}
                    required
                    placeholder="0,00"
                  />
                  <Form.Text className="text-muted">
                    A tendência será calculada automaticamente
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="mb-4">
              <Card.Header className="bg-warning text-white">
                <i className="bi bi-currency-dollar me-2"></i>
                Arroz
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label>Preço (R$/sc 50kg)</Form.Label>
                  <Form.Control
                    type="text"
                    name="arroz"
                    value={displayValues.arroz}
                    onChange={handleChange}
                    required
                    placeholder="0,00"
                  />
                  <Form.Text className="text-muted">
                    A tendência será calculada automaticamente
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="mb-4">
              <Card.Header className="bg-primary text-white">
                <i className="bi bi-currency-dollar me-2"></i>
                Milho
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label>Preço (R$/sc 60kg)</Form.Label>
                  <Form.Control
                    type="text"
                    name="milho"
                    value={displayValues.milho}
                    onChange={handleChange}
                    required
                    placeholder="0,00"
                  />
                  <Form.Text className="text-muted">
                    A tendência será calculada automaticamente
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Importante:</strong> A cotação será cadastrada com a data e hora atuais.
              A cotação mais recente será exibida na página inicial do site.
            </Alert>
          </Col>
        </Row>

        <Row>
          <Col className="d-flex gap-2">
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Salvando...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  Salvar Cotação
                </>
              )}
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => navigate('/admin/quotations')}
              disabled={loading}
            >
              Cancelar
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default QuotationForm;
